import { Colors, Text, TextField, View } from 'react-native-ui-lib';
import React, { useEffect, useState } from 'react';
import { styles } from '../styles';
import { LocalizedStringsContext } from '../localization/localization.native';
import { ScrollView, TextInput } from 'react-native-gesture-handler';
import {
  changeUserEmail,
  changeUserPassword,
  signOut,
  updateUserName,
  useUserName
} from '../firebaseWrapper/firebaseWrapper';
import { navigationProps } from '../typings/types';
import { Pressable } from 'react-native';
import { BottomDialog, NotYetImplementedDialog } from './Dialogs';
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useIsMobile, useIsNotMobile } from '../MediaQueries';
import { alert } from './Alert';
import { ButtonPrimary } from './Buttons';
import { MaterialIcons } from '@expo/vector-icons';

// A trailing-accessory button that toggles a password field between hidden and plain text.
// Mirrors the one Authentication.tsx defines for Login/Registration, kept local here so this
// screen doesn't have to pull in Authentication.tsx's own (unrelated) navigator imports.
function PasswordVisibilityToggle({ showPassword, onPress, strings, testID }: {
  showPassword: boolean,
  onPress: () => void,
  strings: { show_password: string, hide_password: string },
  testID?: string,
}): JSX.Element {
  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={showPassword ? strings.hide_password : strings.show_password}
      onPress={onPress}
      style={{ paddingHorizontal: 10 }}
    >
      <MaterialIcons
        name={showPassword ? 'visibility-off' : 'visibility'}
        size={22}
        color={Colors.iconSecondary}
      />
    </Pressable>
  );
}

// Maps the Firebase Auth error codes that can realistically come back from changeUserPassword /
// changeUserEmail (both reauthenticate first) to a clear, localized message, instead of failing
// silently or showing a raw error code.
function describeAuthError(
  error: any,
  strings: React.ContextType<typeof LocalizedStringsContext>,
): string {
  switch (error?.code) {
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return strings.incorrect_current_password;
    case 'auth/weak-password':
      return strings.weak_password;
    case 'auth/invalid-email':
      return strings.invalid_email;
    case 'auth/email-already-in-use':
      return strings.email_already_in_use;
    default:
      return strings.error + ': ' + (error?.message || error?.code || error);
  }
}

function ChangePasswordDialog({ visible, onDismiss, strings }: {
  visible: boolean,
  onDismiss: () => void,
  strings: React.ContextType<typeof LocalizedStringsContext>,
}): JSX.Element {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
    setShowPasswords(false);
    setSubmitting(false);
  };

  const handleDismiss = () => {
    reset();
    onDismiss();
  };

  const handleSubmit = () => {
    if (submitting) {
      return;
    }
    if (currentPassword === '' || newPassword === '' || confirmNewPassword === '') {
      alert(strings.field_required);
      return;
    }
    if (newPassword !== confirmNewPassword) {
      alert(strings.passwords_do_not_match);
      return;
    }
    setSubmitting(true);
    changeUserPassword(currentPassword, newPassword)
      .then(() => {
        handleDismiss();
        alert(strings.password_changed);
      })
      .catch(error => {
        setSubmitting(false);
        alert(describeAuthError(error, strings));
      });
  };

  return (
    <BottomDialog visible={visible} onDismiss={handleDismiss}>
      <Text h3 marginB-10>{strings.change_password}</Text>
      <TextField
        testID="settings-current-password"
        style={styles.inputContainer}
        secureTextEntry={!showPasswords}
        placeholder={strings.current_password}
        placeholderTextColor={Colors.textSecondary}
        onChangeText={setCurrentPassword}
        value={currentPassword}
        trailingAccessory={
          <PasswordVisibilityToggle
            showPassword={showPasswords}
            onPress={() => setShowPasswords(!showPasswords)}
            strings={strings}
          />
        }
      />
      <TextField
        testID="settings-new-password"
        style={styles.inputContainer}
        secureTextEntry={!showPasswords}
        placeholder={strings.new_password}
        placeholderTextColor={Colors.textSecondary}
        onChangeText={setNewPassword}
        value={newPassword}
      />
      <TextField
        testID="settings-confirm-new-password"
        style={styles.inputContainer}
        secureTextEntry={!showPasswords}
        placeholder={strings.confirm_new_password}
        placeholderTextColor={Colors.textSecondary}
        onChangeText={setConfirmNewPassword}
        value={confirmNewPassword}
      />
      <View marginT-10>
        <ButtonPrimary
          testID="settings-change-password-submit"
          label={strings.change_password}
          muted={submitting}
          onPress={handleSubmit}
        />
      </View>
    </BottomDialog>
  );
}

function ChangeEmailDialog({ visible, onDismiss, strings }: {
  visible: boolean,
  onDismiss: () => void,
  strings: React.ContextType<typeof LocalizedStringsContext>,
}): JSX.Element {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setCurrentPassword('');
    setNewEmail('');
    setShowPassword(false);
    setSubmitting(false);
  };

  const handleDismiss = () => {
    reset();
    onDismiss();
  };

  const handleSubmit = () => {
    if (submitting) {
      return;
    }
    if (currentPassword === '' || newEmail === '') {
      alert(strings.field_required);
      return;
    }
    setSubmitting(true);
    changeUserEmail(currentPassword, newEmail)
      .then(() => {
        handleDismiss();
        alert(strings.verification_email_sent);
      })
      .catch(error => {
        setSubmitting(false);
        alert(describeAuthError(error, strings));
      });
  };

  return (
    <BottomDialog visible={visible} onDismiss={handleDismiss}>
      <Text h3 marginB-10>{strings.change_email}</Text>
      <TextField
        testID="settings-new-email"
        style={styles.inputContainer}
        keyboardType="email-address"
        placeholder={strings.new_email}
        placeholderTextColor={Colors.textSecondary}
        onChangeText={setNewEmail}
        value={newEmail}
      />
      <TextField
        testID="settings-current-password-for-email"
        style={styles.inputContainer}
        secureTextEntry={!showPassword}
        placeholder={strings.current_password}
        placeholderTextColor={Colors.textSecondary}
        onChangeText={setCurrentPassword}
        value={currentPassword}
        trailingAccessory={
          <PasswordVisibilityToggle
            showPassword={showPassword}
            onPress={() => setShowPassword(!showPassword)}
            strings={strings}
          />
        }
      />
      <View marginT-10>
        <ButtonPrimary
          testID="settings-change-email-submit"
          label={strings.change_email}
          muted={submitting}
          onPress={handleSubmit}
        />
      </View>
    </BottomDialog>
  );
}

export function Settings({ navigation, route }: navigationProps): JSX.Element {
  const strings = React.useContext(LocalizedStringsContext);
  const [userName] = useUserName();
  const [userEmail, setUserEmail] = useState('unknown email');
  const isMobile = true // useIsMobile();
  // const isMobile = !useIsNotMobile()

  // The display name field is edited locally and only persisted when "Confirm changes" is
  // pressed (see below), so it needs its own state kept in sync with the fetched userName --
  // but only until the user starts editing it (useUserName() does a one-time fetch, it never
  // fires again on its own).
  const [displayNameInput, setDisplayNameInput] = useState(userName);
  const [displayNameLoaded, setDisplayNameLoaded] = useState(false);

  useEffect(() => {
    if (!displayNameLoaded && userName !== 'unknown user') {
      setDisplayNameInput(userName);
      setDisplayNameLoaded(true);
    }
  }, [userName, displayNameLoaded]);

  const [changePasswordVisible, setChangePasswordVisible] = useState(false);
  const [changeEmailVisible, setChangeEmailVisible] = useState(false);

  const auth = getAuth();
  onAuthStateChanged(auth, (user) => {
    if (user) {
      setUserEmail(user.email)
    }
  });

  const handleCancelChanges = () => {
    setDisplayNameInput(userName);
  };

  const handleConfirmChanges = () => {
    const trimmedName = displayNameInput.trim();
    if (trimmedName === '') {
      alert(strings.field_required);
      return;
    }
    if (trimmedName === userName) {
      return;
    }
    updateUserName(trimmedName)
      .then(() => {
        alert(strings.changes_saved);
      })
      .catch(error => {
        alert(describeAuthError(error, strings));
      });
  };

  const sections = [
    {
      name: strings.account,
      content: [
        {
          label: strings.display_name,
          type: 'text',
          data: displayNameInput,
          onChangeText: (newValue: string) => setDisplayNameInput(newValue),
          handlePress: () => { }
        },
        {
          label: strings.email,
          type: 'text',
          data: userEmail,
          onChangeText: (newValue: string) => { },
          handlePress: () => { }
        },
        {
          label: strings.logout,
          type: 'buttonNormal',
          data: undefined,
          style: {},
          handlePress: () => {
            signOut()
              .then(() => {
                window.location.replace('https//:app.lucid-mind.eu/login');
              });
          }
        },
        {
          label: strings.change_password,
          type: 'buttonNormal',
          data: undefined,
          style: {},
          handlePress: () => setChangePasswordVisible(true)
        },
        {
          label: strings.change_email,
          type: 'buttonNormal',
          data: undefined,
          style: {},
          handlePress: () => setChangeEmailVisible(true)
        }
      ]
    },
    {
      name: strings.danger_zone,
      content: [
        {
          label: strings.delete_account,
          type: 'buttonDanger',
          data: undefined,
          style: {},
          handlePress: () => NotYetImplementedDialog('Deleting account', strings)
        }
      ]
    }
  ]

  return ( // TODO Use section list instead? https://reactnative.dev/docs/sectionlist
    <View flex bg-primaryBG>
      <ScrollView showsVerticalScrollIndicator={false} style={[styles.body, { marginVertical: 5, marginHorizontal: isMobile ? 0 : 5 }]}>
        {sections.map((section) =>
          <View marginB-10 padding-20 style={styles.settingsSection} key={section.name}>
            <Text h3 style={[styles.sectionHeader, { marginBottom: 5 }]}>
              {section.name}
            </Text>
            {section.content.map((sectionContent) =>
              <>
                {sectionContent.type === 'text' ?
                  <View flex row style={[{ marginBottom: 5, alignItems: 'center' }]} key={sectionContent.label}>
                    <Text style={[{ width: '50%' }, styles.textSecondary]}>
                      {sectionContent.label}
                    </Text>
                    <TextInput
                      style={[{ width: '50%', padding: 5 }, styles.textSecondary, styles.borderSecondary]}
                      value={sectionContent.data}
                      onChangeText={sectionContent.onChangeText}
                      keyboardType='default'
                    />
                  </View> : null
                }
              </>
            )}
            <View flex row style={[{ marginBottom: 5, marginTop: 5, flexWrap: 'wrap', rowGap: 5 }]}>
              {section.content.filter((sectionContent) => sectionContent.type === 'buttonNormal' || sectionContent.type === 'buttonDanger').length > 0 ?
                <>
                  {section.content.map((sectionContent) =>
                    <>
                      {sectionContent.type === 'buttonNormal' || sectionContent.type === 'buttonDanger' ?
                        <Pressable
                          onPress={sectionContent.handlePress}
                          style={[
                            styles.buttonSmall,
                            sectionContent.type === 'buttonDanger' ? styles.buttonBorderRed : styles.buttonBorderSecondary,
                            sectionContent.type === 'buttonNormal' && { paddingBottom: 'auto', marginRight: 5, marginBottom: 5 }
                          ]}
                          key={sectionContent.label}
                        >
                          <Text
                            style={[{ width: 'auto' }, sectionContent.type === 'buttonDanger' ? styles.textRed : styles.textSecondary]}
                          >
                            {sectionContent.label}
                          </Text>
                        </Pressable>
                        : null
                      }
                    </>
                  )}
                </>
                : null
              }
            </View>
          </View>
        )}
      </ScrollView>
      <View flex row style={[{
        position: 'absolute',
        right: 0,
        bottom: isMobile ? 45 : 0,
        margin: 15,
        elevation: 5,
        shadowColor: 'black',
        alignItems: 'center',
        justifyContent: 'center',
      }]}
      >
        <Pressable
          testID="settings-cancel-changes"
          style={[{ marginHorizontal: 10 }, styles.buttonBorderSecondary, styles.buttonSmall, styles.backgroundSecondary]}
          onPress={handleCancelChanges}
        >
          <Text style={styles.textSecondary}>
            {strings.cancel_changes}
          </Text>
        </Pressable>
        <Pressable
          testID="settings-confirm-changes"
          style={[styles.buttonBorderRed, styles.buttonSmall, styles.backgroundSecondary]}
          onPress={handleConfirmChanges}
        >
          <Text style={styles.textRed}>
            {strings.confirm_changes}
          </Text>
        </Pressable>
      </View>
      <ChangePasswordDialog
        visible={changePasswordVisible}
        onDismiss={() => setChangePasswordVisible(false)}
        strings={strings}
      />
      <ChangeEmailDialog
        visible={changeEmailVisible}
        onDismiss={() => setChangeEmailVisible(false)}
        strings={strings}
      />
    </View>
  );
}
